// src/features/analytics/helpers.ts
// Pure helpers for the analytics module — scope/entity construction, list
// query params, formatting. No React imports.

import type { Entity } from '@core/types/entity';
import type {
  Aggregation,
  DashboardVisibility,
  ReportDelivery,
  ReportFormat,
  ReportStatus,
  ReportType,
  TimeGranularity,
  TimeRangeType,
  WidgetType,
} from '@core/types/analytics';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

/** Extract an error message suitable for display. */
export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

// The generated List* endpoints type their params as PaginatedRequest, but the
// wire format flattens any plain object one level deep, so extra filter keys
// travel fine as long as we name them after the REST query_mapping key.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function listParams(pagination: { cursor?: string; limit?: number }, filters: Record<string, any> = {}): any {
  return {
    pagination: { cursor: pagination.cursor ?? '', limit: pagination.limit ?? 50, direction: 'forward' },
    ...filters,
  };
}

export function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export function formatDate(iso?: string, locale?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export const WIDGET_TYPES: WidgetType[] = [
  'WIDGET_TYPE_METRIC',
  'WIDGET_TYPE_LINE_CHART',
  'WIDGET_TYPE_BAR_CHART',
  'WIDGET_TYPE_PIE_CHART',
  'WIDGET_TYPE_DONUT_CHART',
  'WIDGET_TYPE_TABLE',
  'WIDGET_TYPE_FUNNEL',
  'WIDGET_TYPE_COHORT',
  'WIDGET_TYPE_HEATMAP',
  'WIDGET_TYPE_MAP',
  'WIDGET_TYPE_PROGRESS',
  'WIDGET_TYPE_COUNTER',
  'WIDGET_TYPE_SPARKLINE',
  'WIDGET_TYPE_AREA_CHART',
  'WIDGET_TYPE_SCATTER_PLOT',
];

export const DASHBOARD_VISIBILITIES: DashboardVisibility[] = [
  'DASHBOARD_VISIBILITY_PRIVATE',
  'DASHBOARD_VISIBILITY_TEAM',
  'DASHBOARD_VISIBILITY_TENANT',
  'DASHBOARD_VISIBILITY_PLATFORM',
];

export const REPORT_TYPES: ReportType[] = [
  'REPORT_TYPE_TABULAR',
  'REPORT_TYPE_SUMMARY',
  'REPORT_TYPE_COMPARISON',
  'REPORT_TYPE_TREND',
  'REPORT_TYPE_CUSTOM',
];

export const REPORT_FORMATS: ReportFormat[] = [
  'REPORT_FORMAT_PDF',
  'REPORT_FORMAT_CSV',
  'REPORT_FORMAT_XLSX',
  'REPORT_FORMAT_JSON',
];

export const REPORT_STATUSES: ReportStatus[] = [
  'REPORT_STATUS_QUEUED',
  'REPORT_STATUS_GENERATING',
  'REPORT_STATUS_READY',
  'REPORT_STATUS_FAILED',
  'REPORT_STATUS_EXPIRED',
];

export const REPORT_DELIVERIES: ReportDelivery[] = [
  'REPORT_DELIVERY_EMAIL',
  'REPORT_DELIVERY_ATTACHMENT_SERVICE',
  'REPORT_DELIVERY_WEBHOOK',
];

export const AGGREGATIONS: Aggregation[] = [
  'AGGREGATION_COUNT',
  'AGGREGATION_SUM',
  'AGGREGATION_AVG',
  'AGGREGATION_MIN',
  'AGGREGATION_MAX',
  'AGGREGATION_DISTINCT_COUNT',
  'AGGREGATION_PERCENTILE',
  'AGGREGATION_MEDIAN',
];

export const TIME_GRANULARITIES: TimeGranularity[] = [
  'TIME_GRANULARITY_MINUTE',
  'TIME_GRANULARITY_HOUR',
  'TIME_GRANULARITY_DAY',
  'TIME_GRANULARITY_WEEK',
  'TIME_GRANULARITY_MONTH',
  'TIME_GRANULARITY_QUARTER',
  'TIME_GRANULARITY_YEAR',
];

export const TIME_RANGE_TYPES: TimeRangeType[] = ['TIME_RANGE_TYPE_RELATIVE', 'TIME_RANGE_TYPE_ABSOLUTE'];

export const FILTER_OPERATORS = ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'in', 'not_in', 'contains'] as const;

/** Widget types that render as a single scalar (metric/counter/progress). */
export function isMetricWidget(t?: WidgetType): boolean {
  return t === 'WIDGET_TYPE_METRIC' || t === 'WIDGET_TYPE_COUNTER' || t === 'WIDGET_TYPE_PROGRESS';
}

/** Widget types that are time-bucketed (line/area/sparkline). */
export function isTimeSeriesWidget(t?: WidgetType): boolean {
  return t === 'WIDGET_TYPE_LINE_CHART' || t === 'WIDGET_TYPE_AREA_CHART' || t === 'WIDGET_TYPE_SPARKLINE';
}

/** Widget types that render as a flat data table. */
export function isTableWidget(t?: WidgetType): boolean {
  return t === 'WIDGET_TYPE_TABLE';
}

/** Widget types that render as pie/donut proportions. */
export function isPieWidget(t?: WidgetType): boolean {
  return t === 'WIDGET_TYPE_PIE_CHART' || t === 'WIDGET_TYPE_DONUT_CHART';
}

export function parseClearableInt(raw: string): number | undefined {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits === '' ? undefined : parseInt(digits, 10);
}
